// src/components/utils/regeneratePDFWithSignature.js
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebaseConfig';
import { prepareReplacementData, replacePlaceholders } from './documentHelpers';
import { generatePDFFromHTML } from './pdfGenerator';

/**
 * Regeneruje PDF z podpisem użytkownika
 * @param {string} userId - ID użytkownika
 * @param {string} documentId - ID dokumentu w kolekcji umowy
 * @param {string} signatureUrl - URL obrazka z podpisem
 * @returns {Promise<string>} - URL nowego PDF z podpisem
 */
export const regeneratePDFWithSignature = async (userId, documentId, signatureUrl) => {
    console.log('🔄 Rozpoczynam regenerację PDF z podpisem...');
    console.log('📝 Parametry:', { userId, documentId, signatureUrl });

    try {
        // 1. Pobierz dokument z kolekcji umowy
        const docRef = doc(db, 'users', userId, 'umowy', documentId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error(`Dokument ${documentId} nie istnieje dla użytkownika ${userId}`);
        }

        const documentData = docSnap.data();
        console.log('📄 Dane dokumentu:', documentData);

        // 2. Znajdź odpowiedni szablon
        let templateData = null;
        let templateId = null;

        // Najpierw spróbuj użyć templateId z dokumentu
        if (documentData.templateId) {
            const templateRef = doc(db, 'documentTemplates', documentData.templateId);
            const templateSnap = await getDoc(templateRef);
            if (templateSnap.exists()) {
                templateData = templateSnap.data();
                templateId = documentData.templateId;
                console.log('✅ Znaleziono szablon po ID:', templateId);
            }
        }

        // Jeśli nie znaleziono, szukaj po nazwie
        if (!templateData) {
            const templatesSnapshot = await getDocs(collection(db, 'documentTemplates'));
            const foundTemplate = templatesSnapshot.docs.find(doc => {
                const data = doc.data();
                return data.name === documentData.documentType;
            });

            if (foundTemplate) {
                templateData = foundTemplate.data();
                templateId = foundTemplate.id;
                console.log('✅ Znaleziono szablon po nazwie:', documentData.documentType);
            } else {
                throw new Error(`Nie znaleziono szablonu dla dokumentu: ${documentData.documentType}`);
            }
        }

        // 3. Przygotuj dane z podpisem
        console.log('📝 Przygotowuję dane z podpisem...');
        const replacements = await prepareReplacementData(userId, true, signatureUrl);

        // 4. Przetwórz HTML z szablonu
        const processedHtml = replacePlaceholders(templateData.content, replacements);

        // Dodaj style i meta tagi
        const fullHtml = `
            <!DOCTYPE html>
            <html lang="pl">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    @page { 
                        size: A4; 
                        margin: 20mm;
                    }
body { 
    font-family: Arial, sans-serif;
    font-size: 10pt;  /* Zmniejszony rozmiar */
    line-height: 1.5;  /* Dostosowana interlinia */
    color: #333;
}
                    .signature-section {
                        margin-top: 40px;
                        margin-bottom: 20px;
                    }
                    .signature-line {
                        border-bottom: 1px dotted #000;
                        width: 100px;
                        margin-top: 40px;
                        display: inline-block;
                    }
.signature-label {
    font-style: italic;
    font-size: 9pt;  /* Zmienione z 11pt */
    margin-top: 5px;
}
                    .signature-image {
                        display: block;
                        margin: 20px 0;
                    }
                    .page-break {
                        page-break-after: always;
                    }
                    @media print {
                        body {
                            margin: 0;
                        }
                    }
                </style>
            </head>
            <body>
                ${processedHtml}
            </body>
            </html>
        `;

        // 5. Generuj nowy PDF
        console.log('🖨️ Generuję nowy PDF z podpisem...');
        const pdfBlob = await generatePDFFromHTML(fullHtml);

        // 6. Usuń stary PDF (opcjonalnie)
        if (documentData.pdfUrl && !documentData.pdfUrlOriginal) {
            try {
                // Zachowaj oryginalny PDF
                console.log('📁 Zachowuję oryginalny PDF...');
            } catch (error) {
                console.warn('⚠️ Nie udało się zachować oryginalnego PDF:', error);
            }
        }

        // 7. Upload nowego PDF
        const timestamp = Date.now();
        const fileName = `documents/${userId}/${documentId}_signed_${timestamp}.pdf`;
        const storageRef = ref(storage, fileName);

        console.log('📤 Uploaduję podpisany PDF:', fileName);
        await uploadBytes(storageRef, pdfBlob, {
            contentType: 'application/pdf',
            customMetadata: {
                userId: userId,
                documentId: documentId,
                signed: 'true',
                signedAt: new Date().toISOString()
            }
        });

        const newPdfUrl = await getDownloadURL(storageRef);
        console.log('✅ Nowy PDF URL:', newPdfUrl);

        // 8. Zaktualizuj dokument w Firestore
        const updateData = {
            pdfUrl: newPdfUrl, // Aktualny PDF (z podpisem)
            signedPdfUrl: newPdfUrl, // Kopia z podpisem
            lastModified: new Date(),
            hasSignedVersion: true
        };

        // Zachowaj oryginalny PDF jeśli jeszcze nie został zachowany
        if (!documentData.pdfUrlOriginal) {
            updateData.pdfUrlOriginal = documentData.pdfUrl;
        }

        await updateDoc(docRef, updateData);

        console.log('✅ PDF z podpisem wygenerowany pomyślnie!');
        return newPdfUrl;

    } catch (error) {
        console.error('❌ Błąd podczas regenerowania PDF z podpisem:', error);
        throw error;
    }
};

/**
 * Regeneruje wszystkie podpisane dokumenty użytkownika
 * (użyteczne przy zmianie szablonów)
 */
export const regenerateAllSignedDocuments = async (userId) => {
    console.log('🔄 Regeneruję wszystkie podpisane dokumenty...');

    try {
        const docsSnapshot = await getDocs(collection(db, 'users', userId, 'umowy'));
        const signedDocs = docsSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.signed && data.signature;
        });

        console.log(`📄 Znaleziono ${signedDocs.length} podpisanych dokumentów`);

        const results = await Promise.allSettled(
            signedDocs.map(async (doc) => {
                const data = doc.data();
                return regeneratePDFWithSignature(userId, doc.id, data.signature);
            })
        );

        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;

        console.log(`✅ Sukces: ${successful}, ❌ Błąd: ${failed}`);

        return {
            total: signedDocs.length,
            successful,
            failed,
            results
        };

    } catch (error) {
        console.error('❌ Błąd podczas regenerowania wszystkich dokumentów:', error);
        throw error;
    }
};